/**
 * Firebase client SDK initialisation for dotperson.org.
 *
 * Targets:
 *   - Auth (email-link / magic-link sign-in for the /admin surface)
 *   - Firestore named DB `dotpersondb` (single named DB pattern from §9 of SHARED_CONTEXT.md)
 *   - Storage (Voices image uploads at gs://gen-lang-client-0994344189.firebasestorage.app/voices/...)
 *   - Analytics (GA4 via Firebase — measurementId in firebase-applet-config.json)
 *
 * The config in firebase-applet-config.json is publicly visible by design —
 * Firebase web config keys are not secrets; security is enforced by Firestore
 * + Storage rules and Auth's domain allowlist.
 */
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import {
  getAnalytics,
  isSupported as analyticsIsSupported,
  type Analytics,
} from "firebase/analytics";
import firebaseConfig from "../../firebase-applet-config.json";

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId);
export const storage = getStorage(app);

export const FIRESTORE_DB_ID = firebaseConfig.firestoreDatabaseId;
export const STORAGE_BUCKET = firebaseConfig.storageBucket;

// Analytics is initialised lazily because:
//   1. SSR / non-browser contexts (build-time prerender) lack window/IndexedDB
//   2. Some browsers / privacy modes block it — `isSupported()` gates that
//   3. A placeholder measurementId (G-REPLACE_ME) must not throw
let analyticsInstance: Analytics | null = null;
let analyticsPromise: Promise<Analytics | null> | null = null;

export function getAnalyticsSafe(): Promise<Analytics | null> {
  if (analyticsPromise) return analyticsPromise;
  analyticsPromise = (async () => {
    if (typeof window === "undefined") return null;
    const measurementId = (firebaseConfig as { measurementId?: string }).measurementId;
    if (!measurementId || measurementId === "G-REPLACE_ME") return null;
    try {
      if (!(await analyticsIsSupported())) return null;
      analyticsInstance = getAnalytics(app);
      return analyticsInstance;
    } catch {
      return null;
    }
  })();
  return analyticsPromise;
}

// Eagerly kick off init in the browser so the GA snippet loads early.
if (typeof window !== "undefined") {
  void getAnalyticsSafe();
}
